/**
 * Perzistencija preferenci (localStorage) i primena enactora na document.documentElement.
 *
 * Enactori utiču na celu stranicu (html klase + CSS varijable), ne samo na panel.
 * @see INTEGRATION.md — „Preferencije i enactori”
 */

import { CONTRAST_OPTIONS, DEFAULTS, FONT_OPTIONS } from './constants';
import { getDefaultConfig } from './defaultConfig';

/** Spaja host config sa podrazumevanim; duboki merge za theme, labels, features. */
export function mergeConfig(userConfig = {}) {
  const defaults = getDefaultConfig();
  return {
    ...defaults,
    ...userConfig,
    theme: { ...defaults.theme, ...userConfig.theme },
    labels: { ...defaults.labels, ...userConfig.labels },
    features: { ...defaults.features, ...userConfig.features },
  };
}

/** Učitava prefs iz localStorage ili vraća DEFAULTS. */
export function readPrefs(config) {
  const merged = mergeConfig(config);
  try {
    const raw = localStorage.getItem(merged.storageKey);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    /* privatni režim / quota */
  }
  return { ...DEFAULTS };
}

export function writePrefs(prefs, config) {
  const merged = mergeConfig(config);
  try {
    localStorage.setItem(merged.storageKey, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

/** Postavlja --ap-theme-* varijable za stil modala (FAB, naslovi sekcija u podrazumevanom režimu). */
export function applyThemeVariables(config) {
  const merged = mergeConfig(config);
  const root = document.documentElement;
  const { theme } = merged;
  root.style.setProperty('--ap-theme-primary', theme.primary);
  root.style.setProperty('--ap-theme-primary-hover', theme.primaryHover);
  root.style.setProperty('--ap-theme-text-muted', theme.textMuted);
  root.style.setProperty('--ap-theme-font-family', theme.fontFamily);
}

/** Stari InfusionUIOptionsBar prefiks — uklanja se pri svakom apply da ne ostane dupla tema. */
const LEGACY_CLASS_PREFIX = 'ogp';

function removeLegacyClasses(root) {
  [...root.classList].forEach((cls) => {
    if (
      cls.startsWith(`${LEGACY_CLASS_PREFIX}-theme-`)
      || cls === `${LEGACY_CLASS_PREFIX}-custom-font`
      || cls === `${LEGACY_CLASS_PREFIX}-enhance-inputs`
    ) {
      root.classList.remove(cls);
    }
  });
  root.style.removeProperty('--ogp-text-size');
  root.style.removeProperty('--ogp-line-spacing');
  root.style.removeProperty('--ogp-font-family');
}

/**
 * Primena svih enactora na <html>.
 * Poziva se pri učitavanju, promeni prefs i resetu.
 */
export function applyToDocument(prefs, config) {
  const merged = mergeConfig(config);
  const prefix = merged.classPrefix;
  const root = document.documentElement;

  removeLegacyClasses(root);

  root.style.setProperty('--ap-text-size', prefs.textSize);
  root.style.setProperty('--ap-line-spacing', prefs.lineSpacing);

  const font = FONT_OPTIONS.find((f) => f.key === prefs.textStyle);
  if (font && font.family) {
    root.style.setProperty('--ap-font-family', font.family);
    root.classList.add(`${prefix}-custom-font`);
  } else {
    root.style.removeProperty('--ap-font-family');
    root.classList.remove(`${prefix}-custom-font`);
  }

  CONTRAST_OPTIONS.forEach((c) => {
    if (c.key !== 'default') root.classList.remove(`${prefix}-theme-${c.key}`);
  });
  if (prefs.contrast !== 'default') {
    root.classList.add(`${prefix}-theme-${prefs.contrast}`);
  }

  root.classList.toggle(`${prefix}-enhance-inputs`, !!prefs.enhanceInputs);
  applyThemeVariables(config);
}
